import clsx from "clsx";
import { FC, ReactNode } from "react";

import { MetaDataGrid } from "@tsmono/inspect-components/content";
import { isNewTabClick, PulsingDots } from "@tsmono/react/components";
import { Icon, IconName } from "@tsmono/react/icons";
import {
  formatDateTime,
  formatTime,
  isVscode,
  parsePackageName,
} from "@tsmono/util";

import { kSandboxSignalName } from "../transform/fixups";
import { EventNode } from "../types";

import styles from "./OutlineRow.module.css";

export interface OutlineRowProps {
  node: EventNode;
  running?: boolean;
  selected?: boolean;
  getEventUrl?: (eventId: string) => string | undefined;
  onSelect?: (nodeId: string) => void;
  /** Called when a URL isn't available but the user clicks to navigate to an event. */
  onNavigateToEvent?: (eventId: string) => void;
  /** Callback-based collapse state. */
  getCollapsed?: (id: string) => boolean;
  setCollapsed?: (id: string, collapsed: boolean) => void;
  /** Optional custom link renderer for deep linking (replaces react-router Link). */
  renderLink?: (url: string, children: ReactNode) => ReactNode;
}

export const OutlineRow: FC<OutlineRowProps> = ({
  node,
  running,
  selected,
  getEventUrl,
  onSelect,
  onNavigateToEvent,
  getCollapsed,
  setCollapsed,
  renderLink,
}) => {
  const collapsed = getCollapsed?.(node.id) ?? false;
  const icon = iconForNode(node);
  const toggle = toggleIcon(node, collapsed);

  // Generate URL for deep linking to this event
  const eventUrl = getEventUrl?.(node.id);

  const labelText = parsePackageName(labelForNode(node)).module;

  const activate = () => {
    onSelect?.(node.id);
    onNavigateToEvent?.(node.id);
  };

  return (
    <>
      <div
        className={clsx(
          styles.eventRow,
          "text-size-smaller",
          selected ? styles.selected : ""
        )}
        style={{ paddingLeft: `${node.depth * 0.75}em` }}
        data-unsearchable={true}
        role="button"
        tabIndex={0}
        onClick={(e) => {
          // A new-tab gesture on the label link opens the event in another
          // tab; don't also jump this transcript to it. (The VS Code webview
          // has no browser tabs, so there the row still jumps.)
          const link =
            e.target instanceof Element ? e.target.closest("a[href]") : null;
          if (link && isNewTabClick(e) && !isVscode()) return;
          activate();
        }}
        onKeyDown={(e) => {
          // Only the row itself: Enter/Space bubbling up from a nested
          // control must keep its own default action.
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            activate();
          }
        }}
      >
        {node.children.length > 0 ? (
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={!collapsed}
            aria-label={`${collapsed ? "Expand" : "Collapse"} ${labelText}`}
            onClick={() => {
              setCollapsed?.(node.id, !collapsed);
            }}
          >
            {toggle ? <Icon name={toggle} /> : undefined}
          </button>
        ) : (
          <div className={styles.toggle} />
        )}
        <div
          className={clsx(styles.label)}
          data-depth={node.depth}
          title={tooltipForNode(node)}
        >
          {icon && (
            <span className={styles.iconSlot}>
              <Icon name={icon} />
            </span>
          )}
          {eventUrl ? (
            renderLink ? (
              <span className={clsx(styles.eventLink)}>
                {renderLink(eventUrl, labelText)}
              </span>
            ) : (
              <a href={eventUrl} className={clsx(styles.eventLink)}>
                {labelText}
              </a>
            )
          ) : (
            <span>{labelText}</span>
          )}
          {running ? (
            <PulsingDots
              size="small"
              className={clsx(styles.progress)}
              subtle={false}
            />
          ) : undefined}
        </div>
      </div>
    </>
  );
};

/** A row rendered below the last outline item while events are still loading. */
export const OutlineLoadingRow: FC = () => (
  <div
    className={clsx(styles.eventRow, styles.loadingRow, "text-size-smaller")}
    style={{ paddingLeft: 0 }}
    data-unsearchable={true}
  >
    <div className={styles.toggle} />
    <div className={clsx(styles.label)} role="status" aria-live="polite">
      <span className={styles.iconSlot}>
        <span className={styles.spinner} aria-hidden="true" />
      </span>
      <span>loading</span>
    </div>
  </div>
);

const toggleIcon = (
  node: EventNode,
  collapsed: boolean
): IconName | undefined => {
  if (node.children.length > 0) {
    return collapsed ? "chevronRight" : "chevronDown";
  }
};

export const iconForNode = (node: EventNode): IconName | undefined => {
  if (node.sourceSpan?.spanType === "agent") {
    return "agent";
  }
  if (node.sourceSpan?.spanType === "branch") {
    return "fork";
  }
  if (node.event.event === "span_begin" && node.event.type === "fork_nav") {
    return "fork";
  }

  switch (node.event.event) {
    case "sample_limit":
      return "limitCustom";

    case "score":
      return "scorer";

    case "error":
      return "error";

    case "compaction":
      return "compaction";
  }
};

/** Tooltip for the outline row (description for agent nodes, undefined otherwise). */
const tooltipForNode = (node: EventNode): string | undefined => {
  if (node.sourceSpan?.spanType === "agent" && node.sourceSpan.description) {
    return node.sourceSpan.description;
  }
};

const labelForNode = (node: EventNode): string => {
  // Agent card nodes: use the lowercase span name
  if (node.sourceSpan?.spanType === "agent") {
    return node.sourceSpan.name.toLowerCase();
  }
  // Branch nodes: use the branch span name
  if (node.sourceSpan?.spanType === "branch") {
    return node.sourceSpan.name.toLowerCase();
  }

  if (node.event.event === "span_begin") {
    switch (node.event.type) {
      case "solver":
        return node.event.name;
      case "tool":
        return node.event.name;
      default: {
        if (node.event.name === kSandboxSignalName) {
          return "sandbox events";
        }
        return node.event.name;
      }
    }
  } else {
    switch (node.event.event) {
      case "subtask":
        return node.event.name;
      case "approval":
        return {
          approve: "approved",
          reject: "rejected",
          escalate: "escalated",
          modify: "modified",
          terminate: "terminated",
        }[node.event.decision];
      case "review":
        return {
          continue: "reviewed",
          escalate: "escalated",
          terminate: "terminated",
        }[node.event.decision];
      case "sentinel":
        return `sentinel ${node.event.status === "reported" ? (node.event.action ?? node.event.kind) : node.event.status}`;
      case "model":
        return `model${node.event.role ? ` (${node.event.role})` : ""}`;
      case "score":
        return "scoring";
      case "step":
        if (node.event.name === kSandboxSignalName) {
          return "sandbox events";
        }
        return node.event.name;
      case "checkpoint":
        return `checkpoint ${node.event.checkpoint_id}`;

      default:
        return node.event.event;
    }
  }
};

export const summarizeNode = (node: EventNode): ReactNode => {
  let entries: Record<string, unknown>;
  switch (node.event.event) {
    case "sample_init":
      entries = {
        sample_id: node.event.sample.id,
        sandbox: node.event.sample.sandbox?.type,
        started: node.event.timestamp
          ? formatDateTime(new Date(node.event.timestamp))
          : undefined,
        working_start: node.event.working_start
          ? formatTime(node.event.working_start)
          : undefined,
      };
      break;

    case "sample_limit":
      entries = {
        type: node.event.type,
        message: node.event.message,
        limit: node.event.limit,
        started: node.event.timestamp
          ? formatDateTime(new Date(node.event.timestamp))
          : undefined,
        working_start: node.event.working_start
          ? formatTime(node.event.working_start)
          : undefined,
      };
      break;
    case "score":
      entries = {
        answer: node.event.score.answer,
        score: node.event.score.value,
        started: node.event.timestamp
          ? formatDateTime(new Date(node.event.timestamp))
          : undefined,
        working_start: node.event.working_start
          ? formatTime(node.event.working_start)
          : undefined,
      };
      break;
    case "span_begin":
      entries = {
        name: node.event.name,
        started: node.event.timestamp
          ? formatDateTime(new Date(node.event.timestamp))
          : undefined,
        working_start: node.event.working_start
          ? formatTime(node.event.working_start)
          : undefined,
      };
      break;
    default:
      entries = {
        started: node.event.timestamp
          ? formatDateTime(new Date(node.event.timestamp))
          : undefined,
        working_start: node.event.working_start
          ? formatTime(node.event.working_start)
          : undefined,
      };
  }

  return (
    <MetaDataGrid
      entries={entries}
      options={{ size: "mini" }}
      className={clsx(styles.popover, "text-size-smallest")}
    />
  );
};
